// Importing necessary libraries and components
import "bootstrap/dist/css/bootstrap.min.css";
import Driver from "pajoohyar-api-driver";
import React, { useContext } from "react";
import ReactDOM from "react-dom";
import { QueryClient, QueryClientProvider } from "react-query";
import querySearch from "stringquery";
import Cookies from "universal-cookie";
import config from "./config.json";
import ContextComponent from "./Context";
import "./index.css";
import Routes from "./Routes";
import {
  getStoredSuccessfulLoginData,
  getStoredProfileData,
  normalizeProfileImageUrl,
  storeProfileData,
  storeSuccessfulLoginData,
} from "./lib/auth";
import * as serviceWorker from "./serviceWorker";

// Creating a new instance of QueryClient for react-query
const queryClient = new QueryClient();
const envApiBaseUrl = process.env.REACT_APP_BASE_URL;

/**
 * Main component of the application.
 * @returns {JSX.Element} The rendered component.
 */
const Main = () => {
  // Using the useContext hook to access the application context
  const context = useContext(ContextComponent);

  // Parsing the query string from the current URL
  const queryStrings = querySearch(window.location.search);

  // Checking if query strings exist and decoding them
  if (
    Object.keys(queryStrings) !== "" ||
    Object.keys(queryStrings) !== "undefined"
  ) {
    const queryKeys = Object.keys(queryStrings);
    queryKeys.forEach(key => {
      queryStrings[key] = decodeURI(queryStrings[key]);
    });
    context.query_parameters = queryStrings;
  }

  // Use the configured Core API directly. This is required when the local
  // frontend is intended to work with the shared Core rather than a locally
  // running backend; Core sends the CORS response for localhost:3000.
  const inferredApiBaseUrl = envApiBaseUrl || config.url;
  context.ApiDriver = new Driver({
    ...config,
    url: inferredApiBaseUrl,
  });

  // Initializing cookies
  context.cookie = new Cookies();

  // Checking for existing authentication cookies
  const access_token = context.cookie.get("pajoohyar_access_token");
  const expires_at = context.cookie.get("pajoohyar_expires_at");
  const cookieProfile = context.cookie.get("pajoohyar_profile");
  let profile = cookieProfile;
  if (profile && typeof profile === "string") {
    try {
      profile = JSON.parse(profile);
    } catch (error) {
      profile = null;
    }
  }
  

  const nowInSeconds = Date.now() / 1000;

  // If authentication cookies exist and are valid, set the context and redirect to the last page
  if (access_token && expires_at && profile && expires_at > nowInSeconds) {
    context.ApiDriver.access_token = access_token;
    context.ApiDriver.expires_at = expires_at;
    context.profile = profile;

    // Older login callbacks only persisted the API session in cookies, while
    // account pages read it from localStorage. Migrate an existing valid
    // cookie session so every part of the site sees the same login state.
    const storedLoginData = getStoredSuccessfulLoginData() || {};
    if (
      storedLoginData.accessToken !== access_token ||
      String(storedLoginData.expiresAt || "") !== String(expires_at)
    ) {
      storeSuccessfulLoginData({
        ...storedLoginData,
        access_token,
        expires_at,
      });
      storeProfileData(profile);
    }
  } else {
    context.cookie.remove("pajoohyar_access_token");
    context.cookie.remove("pajoohyar_expires_at");
    context.cookie.remove("pajoohyar_profile");
  }

  if (!context.profile) {
    const storedLoginData = getStoredSuccessfulLoginData();
    if (storedLoginData) {
      const storedProfileData = getStoredProfileData();
      const nameCandidates = storedProfileData
        ? [
            storedProfileData.user_full_name,
            storedProfileData.displayName,
            storedProfileData.name,
            storedProfileData.preferred_username,
            storedProfileData.username,
            storedProfileData.given_name,
          ]
        : [];
      const resolvedName =
        nameCandidates.find(value => value && String(value).trim()) || "کاربر";
      const avatarCandidates = storedProfileData
        ? [
            storedProfileData.user_full_path_picture,
            storedProfileData.full_path_picture,
            storedProfileData.picture,
          ]
        : [];
      const resolvedAvatar =
        avatarCandidates.map(normalizeProfileImageUrl).find(Boolean) || null;
      context.profile = {
        full_name: resolvedName,
        first_name: resolvedName,
        last_name: "",
        user_id: storedLoginData.userId,
        zotero_key: storedLoginData.key,
        avatar: resolvedAvatar,
      };
      if (
        storedLoginData.accessToken &&
        Number(storedLoginData.expiresAt) > nowInSeconds
      ) {
        context.ApiDriver.access_token = storedLoginData.accessToken;
        context.ApiDriver.expires_at = Number(storedLoginData.expiresAt);
      }
    }
  }

  // Render the Routes component within the QueryClientProvider
  return (
    <QueryClientProvider client={queryClient}>
      <Routes />
    </QueryClientProvider>
  );
};

// Render the Main component into the root element
ReactDOM.render(<Main />, document.getElementById("root"));

// Unregister the service worker
// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
